import { Box, Grid, IconButton, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';

import { FBoxContainer, FHSelect } from '../../../../../../base';
import { FORTA_BLUE } from '../../../../../../../utils/colors';
import CloseIcon from '../../../../../../svgs/CloseIcon';

import type {
  IParentSupervision,
  ISelectOption,
  SelectValueType,
} from '../../../../../../../utils/types';

interface ParentInformationProps {
  isSupervisionCompleted: boolean;
  isEditable: boolean;
  isParentsClearable: boolean;
  parentSupervision: IParentSupervision | null;
  values: IParentSupervision | null;
  parentOneOptions: ISelectOption[];
  parentTwoOptions: ISelectOption[];
  onParentOneUpdate: (parentId: number) => void;
  onParentTwoUpdate: (parentId: number) => void;
  onClearParentOne: () => void;
  onClearParentTwo: () => void;
}

function ParentInformation({
  isSupervisionCompleted,
  isEditable,
  isParentsClearable,
  parentSupervision,
  values,
  parentOneOptions,
  parentTwoOptions,
  onParentOneUpdate,
  onParentTwoUpdate,
  onClearParentOne,
  onClearParentTwo,
}: ParentInformationProps) {
  return (
    <Grid container mt={2}>
      <Grid item md={5.9} xs={12}>
        <FBoxContainer sx={{ boxShadow: 'none', borderRadius: '20px', height: 220 }}>
          <Typography
            fontSize={21}
            mt={0.5}
            mb={isSupervisionCompleted ? 5 : 3}
            color={FORTA_BLUE.primary}
            textAlign="left"
          >
            Parent information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={5.5} mb={2}>
              <Typography sx={{ mb: 1 }} fontWeight={700} textAlign="left">
                Parent 1
              </Typography>
              <Box sx={{ display: 'flex' }}>
                {isSupervisionCompleted ? (
                  <Typography
                    textAlign="left"
                    data-testid="parentSNParentOneName"
                    sx={{ wordBreak: 'break-word', mr: 1 }}
                  >
                    {parentSupervision?.parent_one_parent_and_caregiver?.name}
                  </Typography>
                ) : (
                  <FHSelect
                    data-testid="parentSNParentOneField"
                    sx={{ width: '100%', boxShadow: 'none', borderRadius: '8px' }}
                    inputHeight={40}
                    disabled={!isEditable}
                    options={parentOneOptions}
                    optionDataTestIdPrefix="parentSNParentOneField"
                    value={
                      values?.parent_one_parent_and_caregiver_id ??
                      values?.parent_one_parent_and_caregiver?.id ??
                      ''
                    }
                    onUpdate={(value: SelectValueType) => {
                      const parentId = Number(value);
                      if (Number.isFinite(parentId)) onParentOneUpdate(parentId);
                    }}
                  />
                )}
                {isParentsClearable &&
                  (values?.parent_one_parent_and_caregiver_id ??
                    values?.parent_one_parent_and_caregiver?.id) && (
                  <IconButton
                    sx={{ position: 'absolute', right: 30, padding: 0, top: 10 }}
                    onClick={onClearParentOne}
                  >
                    <CloseIcon color={FORTA_BLUE.primary} />
                  </IconButton>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={5.5} mb={2}>
              <Typography sx={{ mb: 1 }} fontWeight={700} textAlign="left">
                Parent 2
              </Typography>
              <Box sx={{ display: 'flex', position: 'relative', flexGrow: 1 }}>
                {isSupervisionCompleted ? (
                  <Typography
                    textAlign="left"
                    data-testid="parentSNParentOneName"
                    sx={{ wordBreak: 'break-word', mr: 1 }}
                  >
                    {parentSupervision?.parent_two_parent_and_caregiver?.name ?? 'N/A'}
                  </Typography>
                ) : (
                  <FHSelect
                    data-testid="parentSNParentTwoField"
                    sx={{ width: '100%', boxShadow: 'none', borderRadius: '8px' }}
                    inputHeight={40}
                    disabled={
                      !isEditable ||
                      !(values?.parent_one_parent_and_caregiver_id ??
                        values?.parent_one_parent_and_caregiver?.id)
                    }
                    options={parentTwoOptions}
                    optionDataTestIdPrefix="parentSNParentTwoField"
                    value={
                      values?.parent_two_parent_and_caregiver_id ??
                      values?.parent_two_parent_and_caregiver?.id ??
                      ''
                    }
                    onUpdate={(value: SelectValueType) => {
                      const parentId = Number(value);
                      if (Number.isFinite(parentId)) onParentTwoUpdate(parentId);
                    }}
                    selectFirstOption={false}
                  />
                )}
                {isEditable &&
                  (values?.parent_two_parent_and_caregiver_id ??
                    values?.parent_two_parent_and_caregiver?.id) && (
                  <IconButton
                    sx={{ position: 'absolute', right: 30, padding: 0, top: 10 }}
                    onClick={onClearParentTwo}
                  >
                    <Close sx={{ width: 18, height: 18, mt: 0.2 }} />
                  </IconButton>
                )}
              </Box>
            </Grid>
          </Grid>
        </FBoxContainer>
      </Grid>
    </Grid>
  );
}

export default ParentInformation;
